import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingFlowApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Link2, Copy, Eye, Trash2, CheckCircle2, Clock, Send,
  Loader2, Building2, Cpu, Users, UserCheck, ChevronDown, ChevronUp,
  Pencil, Zap, X, Save, Download, AlertTriangle,
  BellPlus,
} from 'lucide-react';

const STATUS_MAP = {
  pending: { label: 'Väntar', color: 'bg-slate-100 text-slate-600' },
  submitted: { label: 'Inskickad', color: 'bg-blue-100 text-blue-700' },
  activated: { label: 'Aktiverad', color: 'bg-emerald-100 text-emerald-700' },
};

export default function OnboardingSubmissionsPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLink, setNewLink] = useState({ max_sensors: 14, customer_label: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [activating, setActivating] = useState(null);
  const [creatingRules, setCreatingRules] = useState(null);
  const [activationResult, setActivationResult] = useState(null);

  useEffect(() => { loadSubmissions(); }, []);

  const loadSubmissions = async () => {
    try {
      const res = await onboardingFlowApi.listSubmissions();
      setSubmissions(res.data.submissions || []);
    } catch { toast.error('Kunde inte ladda'); }
    finally { setLoading(false); }
  };

  const handleCreateLink = async () => {
    setCreating(true);
    try {
      const res = await onboardingFlowApi.createLink(newLink);
      toast.success('Onboarding-länk skapad');
      setShowCreate(false);
      setNewLink({ max_sensors: 14, customer_label: '' });
      loadSubmissions();
    } catch { toast.error('Kunde inte skapa länk'); }
    finally { setCreating(false); }
  };

  const handleCopyLink = (token) => {
    const url = `${window.location.origin}/onboard/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Länk kopierad till urklipp');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ta bort denna inlämning?')) return;
    try {
      await onboardingFlowApi.deleteSubmission(id);
      toast.success('Borttagen');
      loadSubmissions();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const handleStartEdit = (sub) => {
    setEditingId(sub.id);
    setEditData({ ...sub });
  };

  const handleDownloadPdf = async (sub) => {
    try {
      const res = await onboardingFlowApi.submissionPdf(sub.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (sub.school_name || 'onboarding').replace(/\s+/g, '_').toLowerCase();
      a.download = `vapelarm_registrering_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF-kvitto nedladdat');
    } catch {
      toast.error('Kunde inte ladda ner PDF');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await onboardingFlowApi.updateSubmission(editingId, editData);
      toast.success('Uppdaterad');
      setEditingId(null);
      setEditData(null);
      loadSubmissions();
    } catch { toast.error('Kunde inte spara'); }
  };

  const handleActivate = async (sub, createUsers) => {
    setActivating(sub.id);
    try {
      const res = await onboardingFlowApi.activateSubmission(sub.id, { create_users: createUsers });
      setActivationResult(res.data);
      toast.success('Skolan har aktiverats!');
      loadSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte aktivera');
    } finally { setActivating(null); }
  };

  const handleCreateRules = async (sub) => {
    setCreatingRules(sub.id);
    try {
      const res = await onboardingFlowApi.createAlarmRules(sub.id);
      const { rules_created, already_existed } = res.data;
      if (rules_created > 0) toast.success(`${rules_created} ${rules_created === 1 ? 'larmregel' : 'larmregler'} skapades${already_existed ? ` (${already_existed} fanns redan)` : ''}`);
      else toast.info(already_existed ? 'Alla larmregler fanns redan' : 'Inga användare är markerade med LARM');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa larmregler');
    } finally { setCreatingRules(null); }
  };

  const baseUrl = window.location.origin;

  if (loading) {
    return <div className="p-6 md:p-10 flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="onboarding-submissions-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl text-navy dark:text-white tracking-tight">Kundregistrering</h1>
          <p className="text-slate-500 dark:text-slate-400 font-ibm text-sm mt-1">Skapa onboarding-länkar och hantera inlämningar från nya kunder</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="gap-2 shrink-0" data-testid="create-link-btn">
          <Plus className="h-4 w-4" /> Ny onboarding-länk
        </Button>
      </div>

      {/* Create link form */}
      {showCreate && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-chivo font-bold text-lg text-navy dark:text-white">Skapa ny länk</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="font-ibm text-xs">Kundnamn / etikett</Label>
                <Input value={newLink.customer_label} onChange={e => setNewLink(p => ({ ...p, customer_label: e.target.value }))} placeholder="T.ex. Torsby kommun - Frykenskolan" className="mt-1" data-testid="link-label" />
              </div>
              <div>
                <Label className="font-ibm text-xs">Max antal sensorer</Label>
                <Input type="number" value={newLink.max_sensors} onChange={e => setNewLink(p => ({ ...p, max_sensors: parseInt(e.target.value) || 14 }))} className="mt-1" data-testid="link-max-sensors" />
              </div>
              <div>
                <Label className="font-ibm text-xs">Utgångsdatum (valfritt)</Label>
                <Input type="date" value={newLink.expires_at || ''} onChange={e => setNewLink(p => ({ ...p, expires_at: e.target.value }))} className="mt-1" data-testid="link-expires" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Avbryt</Button>
              <Button onClick={handleCreateLink} disabled={creating} className="gap-2" data-testid="confirm-create-btn">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Skapa länk
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activation result modal */}
      {activationResult && (
        <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-chivo font-bold text-lg text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" /> Aktivering klar
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setActivationResult(null)}><X className="h-4 w-4" /></Button>
            </div>
            <p className="text-sm font-ibm text-slate-600">
              <strong>{activationResult.school?.name}</strong> — {activationResult.rooms_created} rum, {activationResult.devices_created} enheter, {activationResult.rules_created ?? 0} larmregler skapade.
            </p>
            {activationResult.users_created?.length > 0 && (
              <div>
                <p className="text-xs font-ibm font-semibold text-slate-500 uppercase mt-2">Skapade användare</p>
                <div className="mt-1 space-y-1">
                  {activationResult.users_created.map((u, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs font-ibm bg-white rounded px-3 py-2 border">
                      <span className="font-semibold text-navy">{u.name}</span>
                      <span className="text-slate-500">{u.email}</span>
                      <span className="text-slate-400">({u.role})</span>
                      {u.temp_password && (
                        <span className="ml-auto font-mono bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-200">{u.temp_password}</span>
                      )}
                      {u.email_sent === true && <span className="text-emerald-600 text-[10px] font-semibold">MAIL SKICKAT</span>}
                      {u.email_sent === false && <span className="text-red-500 text-[10px] font-semibold">MAIL EJ SKICKAT</span>}
                      {u.status === 'redan_finns' && <span className="ml-auto text-slate-400">Finns redan</span>}
                    </div>
                  ))}
                </div>
                <p className="text-xs font-ibm text-amber-600 mt-2">Kopiera tillfälliga lösenord och skicka till användarna. De bör byta lösenord vid första inloggning.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submissions list */}
      <div className="space-y-3">
        {submissions.length === 0 && (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <Link2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-ibm">Inga onboarding-länkar skapade ännu</p>
            </CardContent>
          </Card>
        )}

        {submissions.map(sub => {
          const status = STATUS_MAP[sub.status] || STATUS_MAP.pending;
          const isExpanded = expandedId === sub.id;
          const isEditing = editingId === sub.id;
          const link = `${baseUrl}/onboard/${sub.token}`;

          return (
            <Card key={sub.id} className="border-slate-200 dark:border-slate-700" data-testid={`submission-${sub.id}`}>
              <CardContent className="p-4">
                {/* Header row */}
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : sub.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-ibm font-semibold text-sm text-navy dark:text-white">
                        {sub.school_name || sub.customer_label || 'Ny kund'}
                      </p>
                      <Badge className={`text-[10px] ${status.color}`}>{status.label}</Badge>
                      <span className="text-[10px] font-ibm text-slate-400">{sub.max_sensors} sensorer</span>
                      {sub.expires_at && (
                        <span className={`text-[10px] font-ibm ${new Date(sub.expires_at) < new Date() ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                          {new Date(sub.expires_at) < new Date() ? 'Utgången' : `Giltig t.o.m. ${new Date(sub.expires_at).toLocaleDateString('sv-SE')}`}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 font-ibm mt-0.5">
                      Skapad {new Date(sub.created_at).toLocaleDateString('sv-SE')}
                      {sub.submitted_at && ` · Inskickad ${new Date(sub.submitted_at).toLocaleDateString('sv-SE')}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => { e.stopPropagation(); handleCopyLink(sub.token); }} title="Kopiera länk">
                      <Copy className="h-4 w-4 text-slate-400" />
                    </Button>
                    {sub.status !== 'activated' && (
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => { e.stopPropagation(); handleDelete(sub.id); }} title="Ta bort">
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    )}
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && !isEditing && (
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 space-y-3">
                    {/* Link */}
                    <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2">
                      <Link2 className="h-4 w-4 text-slate-400 shrink-0" />
                      <code className="text-xs font-ibm text-slate-600 dark:text-slate-300 truncate flex-1">{link}</code>
                      <Button variant="ghost" size="sm" className="h-7 shrink-0" onClick={() => handleCopyLink(sub.token)}>Kopiera</Button>
                    </div>

                    {sub.school_name && (
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div><p className="text-[10px] font-ibm text-slate-400 uppercase">Skola</p><p className="text-sm font-ibm text-navy dark:text-white font-semibold">{sub.school_name}</p><p className="text-xs text-slate-500">{sub.school_address}</p></div>
                        <div><p className="text-[10px] font-ibm text-slate-400 uppercase">Huvudkontakt</p><p className="text-sm font-ibm text-navy dark:text-white font-semibold">{sub.primary_contact?.name || '–'}</p><p className="text-xs text-slate-500">{sub.primary_contact?.email} · {sub.primary_contact?.phone}</p></div>
                      </div>
                    )}

                    {sub.departments?.filter(d => d.name).length > 0 && (
                      <div><p className="text-[10px] font-ibm text-slate-400 uppercase">Avdelningar</p><p className="text-sm font-ibm text-slate-600">{sub.departments.filter(d => d.name).map(d => d.name).join(', ')}</p></div>
                    )}

                    {sub.sensors?.filter(s => s.name).length > 0 && (
                      <div>
                        <p className="text-[10px] font-ibm text-slate-400 uppercase">Sensorer ({sub.sensors.filter(s => s.name).length})</p>
                        <div className="mt-1 grid gap-1 sm:grid-cols-2">
                          {sub.sensors.filter(s => s.name).map((s, i) => (
                            <div key={i} className="text-xs font-ibm text-slate-600 flex items-center gap-1.5">
                              <Cpu className="h-3 w-3 text-slate-400" />
                              {s.name}
                              {s.department && <span className="text-slate-400">({s.department})</span>}
                              <span className={`ml-auto text-[10px] ${s.type === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}>{s.type === 'active' ? 'Aktiv' : 'Skal'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {sub.users?.filter(u => u.name).length > 0 && (
                      <div>
                        <p className="text-[10px] font-ibm text-slate-400 uppercase">Användare ({sub.users.filter(u => u.name).length})</p>
                        <div className="mt-1 space-y-1">
                          {sub.users.filter(u => u.name).map((u, i) => (
                            <div key={i} className="text-xs font-ibm text-slate-600 flex items-center gap-2">
                              <Users className="h-3 w-3 text-slate-400" />
                              <span className="font-semibold">{u.name}</span>
                              <span className="text-slate-400">{u.email}</span>
                              <span className="text-slate-400">({u.role})</span>
                              {u.receives_alarms && <span className="text-blue-500 text-[10px]">LARM</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {sub.notes && <div><p className="text-[10px] font-ibm text-slate-400 uppercase">Anteckningar</p><p className="text-sm font-ibm text-slate-600">{sub.notes}</p></div>}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      {(sub.school_name || sub.status === 'submitted' || sub.status === 'activated') && (
                        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => handleDownloadPdf(sub)} data-testid={`pdf-sub-${sub.id}`}>
                          <Download className="h-3.5 w-3.5" /> PDF-kvitto
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => handleStartEdit(sub)} data-testid={`edit-sub-${sub.id}`}>
                        <Pencil className="h-3.5 w-3.5" /> Redigera
                      </Button>
                      {sub.status !== 'activated' && (
                        <>
                          <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleActivate(sub, false)} disabled={activating === sub.id} data-testid={`activate-sub-${sub.id}`}>
                            {activating === sub.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                            Aktivera (utan konton)
                          </Button>
                          <Button size="sm" className="gap-1.5" onClick={() => handleActivate(sub, true)} disabled={activating === sub.id} data-testid={`activate-users-${sub.id}`}>
                            {activating === sub.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
                            Aktivera + skapa konton
                          </Button>
                        </>
                      )}
                      {sub.status === 'activated' && (
                        <>
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" /> Aktiverad</Badge>
                          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => handleCreateRules(sub)} disabled={creatingRules === sub.id} data-testid={`create-rules-${sub.id}`}>
                            {creatingRules === sub.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellPlus className="h-3.5 w-3.5" />}
                            Skapa larmregler
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Edit mode — full form */}
                {isEditing && editData && (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-5">
                    {sub.status === 'activated' && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        Denna registrering ar redan aktiverad. Andringar har paverkar INTE redan skapade skolor/rum/enheter — de maste andras separat.
                      </div>
                    )}

                    {/* School info */}
                    <div>
                      <p className="text-[10px] font-ibm text-slate-400 uppercase font-semibold mb-2">Skolinformation</p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label className="font-ibm text-xs">Skolans namn</Label>
                          <Input value={editData.school_name || ''} onChange={e => setEditData(p => ({ ...p, school_name: e.target.value }))} className="mt-1" data-testid="edit-school-name" />
                        </div>
                        <div>
                          <Label className="font-ibm text-xs">Adress</Label>
                          <Input value={editData.school_address || ''} onChange={e => setEditData(p => ({ ...p, school_address: e.target.value }))} className="mt-1" data-testid="edit-school-address" />
                        </div>
                        <div>
                          <Label className="font-ibm text-xs">Kundetikett</Label>
                          <Input value={editData.customer_label || ''} onChange={e => setEditData(p => ({ ...p, customer_label: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                          <Label className="font-ibm text-xs">Max sensorer</Label>
                          <Input type="number" value={editData.max_sensors || 14} onChange={e => setEditData(p => ({ ...p, max_sensors: parseInt(e.target.value) || 14 }))} className="mt-1" />
                        </div>
                      </div>
                    </div>

                    {/* Primary contact */}
                    <div>
                      <p className="text-[10px] font-ibm text-slate-400 uppercase font-semibold mb-2">Huvudkontakt</p>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                          <Label className="font-ibm text-xs">Namn</Label>
                          <Input value={editData.primary_contact?.name || ''} onChange={e => setEditData(p => ({ ...p, primary_contact: { ...p.primary_contact, name: e.target.value } }))} className="mt-1" />
                        </div>
                        <div>
                          <Label className="font-ibm text-xs">E-post</Label>
                          <Input value={editData.primary_contact?.email || ''} onChange={e => setEditData(p => ({ ...p, primary_contact: { ...p.primary_contact, email: e.target.value } }))} className="mt-1" />
                        </div>
                        <div>
                          <Label className="font-ibm text-xs">Telefon</Label>
                          <Input value={editData.primary_contact?.phone || ''} onChange={e => setEditData(p => ({ ...p, primary_contact: { ...p.primary_contact, phone: e.target.value } }))} className="mt-1" />
                        </div>
                      </div>
                    </div>

                    {/* Departments */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-ibm text-slate-400 uppercase font-semibold">Avdelningar ({(editData.departments || []).filter(d => d.name).length})</p>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setEditData(p => ({ ...p, departments: [...(p.departments || []), { name: '' }] }))}>
                          <Plus className="h-3 w-3" /> Lagg till
                        </Button>
                      </div>
                      <div className="space-y-1.5">
                        {(editData.departments || []).map((dept, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input value={dept.name || ''} placeholder="Avdelningsnamn" className="h-8 text-xs"
                              onChange={e => { const d = [...(editData.departments || [])]; d[i] = { ...d[i], name: e.target.value }; setEditData(p => ({ ...p, departments: d })); }} />
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-red-400 hover:text-red-600"
                              onClick={() => { const d = [...(editData.departments || [])]; d.splice(i, 1); setEditData(p => ({ ...p, departments: d })); }}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Sensors */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-ibm text-slate-400 uppercase font-semibold">Sensorer ({(editData.sensors || []).filter(s => s.name).length})</p>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setEditData(p => ({ ...p, sensors: [...(p.sensors || []), { name: '', department: '', type: 'active' }] }))} data-testid="add-sensor-btn">
                          <Plus className="h-3 w-3" /> Lagg till sensor
                        </Button>
                      </div>
                      <div className="space-y-1.5">
                        {(editData.sensors || []).map((sensor, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input value={sensor.name || ''} placeholder="Plats / rumnamn" className="h-8 text-xs flex-1"
                              onChange={e => { const s = [...(editData.sensors || [])]; s[i] = { ...s[i], name: e.target.value }; setEditData(p => ({ ...p, sensors: s })); }} />
                            <Input value={sensor.department || ''} placeholder="Avdelning" className="h-8 text-xs w-28"
                              onChange={e => { const s = [...(editData.sensors || [])]; s[i] = { ...s[i], department: e.target.value }; setEditData(p => ({ ...p, sensors: s })); }} />
                            <select className="h-8 text-xs border rounded px-1 bg-background" value={sensor.type || 'active'}
                              onChange={e => { const s = [...(editData.sensors || [])]; s[i] = { ...s[i], type: e.target.value }; setEditData(p => ({ ...p, sensors: s })); }}>
                              <option value="active">Aktiv</option>
                              <option value="shell">Skal</option>
                            </select>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-red-400 hover:text-red-600"
                              onClick={() => { const s = [...(editData.sensors || [])]; s.splice(i, 1); setEditData(p => ({ ...p, sensors: s })); }}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Users */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-ibm text-slate-400 uppercase font-semibold">Anvandare ({(editData.users || []).filter(u => u.name).length})</p>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setEditData(p => ({ ...p, users: [...(p.users || []), { name: '', email: '', role: 'personal', receives_alarms: false }] }))} data-testid="add-user-btn">
                          <Plus className="h-3 w-3" /> Lagg till anvandare
                        </Button>
                      </div>
                      <div className="space-y-1.5">
                        {(editData.users || []).map((user, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input value={user.name || ''} placeholder="Namn" className="h-8 text-xs w-32"
                              onChange={e => { const u = [...(editData.users || [])]; u[i] = { ...u[i], name: e.target.value }; setEditData(p => ({ ...p, users: u })); }} />
                            <Input value={user.email || ''} placeholder="E-post" className="h-8 text-xs flex-1"
                              onChange={e => { const u = [...(editData.users || [])]; u[i] = { ...u[i], email: e.target.value }; setEditData(p => ({ ...p, users: u })); }} />
                            <select className="h-8 text-xs border rounded px-1 bg-background w-24" value={user.role || 'personal'}
                              onChange={e => { const u = [...(editData.users || [])]; u[i] = { ...u[i], role: e.target.value }; setEditData(p => ({ ...p, users: u })); }}>
                              <option value="rektor">Rektor</option>
                              <option value="skoladmin">Skoladmin</option>
                              <option value="personal">Personal</option>
                              <option value="it">IT</option>
                              <option value="vaktmastare">Vaktmastare</option>
                            </select>
                            <label className="flex items-center gap-1 text-[10px] text-slate-500 shrink-0">
                              <input type="checkbox" checked={user.receives_alarms || false}
                                onChange={e => { const u = [...(editData.users || [])]; u[i] = { ...u[i], receives_alarms: e.target.checked }; setEditData(p => ({ ...p, users: u })); }} />
                              Larm
                            </label>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-red-400 hover:text-red-600"
                              onClick={() => { const u = [...(editData.users || [])]; u.splice(i, 1); setEditData(p => ({ ...p, users: u })); }}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notes + Installation info */}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="font-ibm text-xs">Anteckningar</Label>
                        <Textarea value={editData.notes || ''} onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))} className="mt-1" rows={2} />
                      </div>
                      <div>
                        <Label className="font-ibm text-xs">Installationsinformation</Label>
                        <Textarea value={editData.installation_info || ''} onChange={e => setEditData(p => ({ ...p, installation_info: e.target.value }))} className="mt-1" rows={2} />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={() => { setEditingId(null); setEditData(null); }}>Avbryt</Button>
                      <Button size="sm" className="gap-1.5" onClick={handleSaveEdit} data-testid="save-edit-btn"><Save className="h-3.5 w-3.5" /> Spara andringar</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
